import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { add_section, remove_entry, remove_section, rename_section, set_current_section } from "../../actions/ExpenseBucket";
import { delete_slot } from "../../actions/Slots";
import stylelist from "stylelist";
import MinutesToTime from "./MinutesToTime";
import axios from "axios";
import SyncConstants from "../../constants/SyncConstants";
import ConfirmButton from "./ConfirmButton";
import moment from "moment";
import useAuthentication from "../../hooks/useAuthentication";
import WeClappCustomerSelector from "../WeClapp/WeClappCustomerSelector";

const ExpenseBucketSection = (props) => {
    const dispatch = useDispatch();
    const ExpenseBucket = useSelector((state) => state.ExpenseBucket);
    const UserSettings = useSelector((state) => state.User);

    const [editName, setEditName] = useState(false);
    const [expandView, setExpandView] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const Auth = useAuthentication();
    const [currentModalTab, setCurrentModalTab] = useState("invoice");

    const [currentMinuteSum, setCurrentMinuteSum] = useState(0);
    const [textFormat, setTextFormat] = useState("");
    const [csvFormat, setCsvFormat] = useState("");

    const [weclappDescription, setWeclappDescription] = useState("");
    const [weclappCustomerId, setWeclappCustomerId] = useState("");
    const [weclappCustomerName, setWeclappCustomerName] = useState("");
    const [weclappArticleId, setWeclappArticleId] = useState("1916");
    const [directExportToWeClapp, setDirectExportToWeClapp] = useState(false);
    const [showCustomerSelector, setShowCustomerSelector] = useState(false);

    const MinutesToTimeString = (minutes) => {
        return Math.floor(minutes / 60).toString().padStart(2, "0") + ":" + (minutes % 60).toString().padStart(2, "0");
    }

    useEffect(() => {
        let newMinuteSum = 0;
        let newString = "";
        let csv = [];

        // Initialisiere CSV-Header
        csv.push(["Kommentar", "Person", "Datum", "Dauer"].join("\t"));

        // Gruppe Expenses nach Breadcrumb und erfasse Summen pro Breadcrumb
        const groupedByBreadcrumb = {};
        let maxBreadcrumbLength = 0;
        let customerWeclappId = "";
        let customerName = "";
        let firstEntryDate = null;

        ExpenseBucket.sections[props.uuid].expenses.forEach(e => {
            // Check if this is an external cost entry
            if (e.type === 'externalcost') {
                // For external costs, we don't add minutes but track them separately
                // We'll handle them in the WeClapp export
            } else {
                newMinuteSum += parseInt(e.minutes);
            }

            // Speichere Datum der ersten Buchung
            if (!firstEntryDate || e.date < firstEntryDate) {
                firstEntryDate = e.date;
            }

            // Extrahiere WeClapp-Kundennummer vom ersten Eintrag, falls vorhanden
            if (!customerWeclappId && e.customer_weclapp_id) {
                customerWeclappId = e.customer_weclapp_id;
                // Extrahiere Kundennamen aus Breadcrumb (erster Teil vor dem ersten ">")
                if (e.breadcrumb) {
                    customerName = e.breadcrumb.split(' > ')[0];
                }
            }

            // Finde die Länge des längsten Breadcrumb
            if (e.breadcrumb && e.breadcrumb.length > maxBreadcrumbLength) {
                maxBreadcrumbLength = e.breadcrumb.length;
            }

            // Gruppe nach Breadcrumb sortieren und Summen erfassen
            const breadcrumbKey = e.breadcrumb || 'Externe Kosten';
            if (!groupedByBreadcrumb[breadcrumbKey]) {
                groupedByBreadcrumb[breadcrumbKey] = {
                    expenses: [],
                    totalMinutes: 0,
                    totalAmount: 0  // For external costs
                };
            }

            // Füge die Ausgabe zur entsprechenden Breadcrumb-Gruppe hinzu
            groupedByBreadcrumb[breadcrumbKey].expenses.push(e);
            
            // Handle minutes or amount based on entry type
            if (e.type === 'externalcost') {
                groupedByBreadcrumb[breadcrumbKey].totalAmount += parseFloat(e.amount || 0);
            } else {
                groupedByBreadcrumb[breadcrumbKey].totalMinutes += parseInt(e.minutes);
            }
        });

        // Setze aktuelle Minute-Summe
        setCurrentMinuteSum(newMinuteSum);

        // Setze WeClapp-Kundennummer und Beschreibung, falls gefunden
        if (customerWeclappId && customerName) {
            if (!weclappCustomerId) {
                setWeclappCustomerId(customerWeclappId);
                setWeclappCustomerName(customerName);
            }
            
            // Generiere Rechnungsbeschreibung: "Kundenname // Rechnung MONAT JAHR"
            // Nutze Datum der ersten Buchung statt aktuellem Monat
            if ((!weclappDescription || weclappDescription === "") && firstEntryDate) {
                moment.locale('de');
                const entryMoment = moment(firstEntryDate);
                const month = entryMoment.format('MMMM'); // z.B. "Januar"
                const year = entryMoment.format('YYYY');
                const defaultDescription = `${customerName} // Rechnung ${month} ${year}`;
                setWeclappDescription(defaultDescription);
            }
        }
    
        // Erzeuge eine Trennlinie basierend auf der Länge des längsten Breadcrumbs
        const createSeparator = () => "=".repeat(maxBreadcrumbLength * 0.5); // 20 als Puffer, um Platz für den Text zu haben
    
        // Verarbeite die gruppierten Einträge in CSV und Text
        Object.keys(groupedByBreadcrumb).forEach(breadcrumb => {
            const breadcrumbData = groupedByBreadcrumb[breadcrumb];
    
            // Format summary based on whether we have minutes or amount
            let summary = '';
            if (breadcrumbData.totalMinutes > 0) {
                summary = `Gesamt: ${MinutesToTimeString(breadcrumbData.totalMinutes)}`;
            }
            if (breadcrumbData.totalAmount > 0) {
                summary += (summary ? ' + ' : 'Gesamt: ') + `${breadcrumbData.totalAmount.toFixed(2)} €`;
            }
            
            // Füge den Breadcrumb als eigene Zeile hinzu (ohne separate Spalte)
            csv.push([breadcrumb, "", "", summary].join("\t"));
    
            newString += createSeparator() + "\n";
            newString += "" + breadcrumb + " (" + summary + ")\n";
            newString += createSeparator() + "\n\n";
            
            // Füge alle zugehörigen Einträge hinzu
            breadcrumbData.expenses.forEach(e => {
                if (e.type === 'externalcost') {
                    // External cost entry
                    csv.push([
                        e.description.replace('\t', ''),
                        e.person,
                        e.date,
                        `${parseFloat(e.amount).toFixed(2)} €`,
                    ].join("\t"));
        
                    newString += "-  " + e.description.replace('\t', '') + ": " + parseFloat(e.amount).toFixed(2) + " €\n"
                    newString += "   Mitarbeiter: " + e.person + "\n";
                    newString += "   Datum: " + moment(e.date).format("DD.MM.Y") + "\n";
                    if (e.invoice_number) {
                        newString += "   Rechnung: " + e.invoice_number + "\n";
                    }
                    newString += "\n";
                } else {
                    // Regular expense entry
                    csv.push([
                        e.comment.replace('\t', ''),
                        e.person,
                        e.date,
                        MinutesToTimeString(e.minutes),
                    ].join("\t"));
        
                    newString += "-  " + e.comment.replace('\t', '') + ": " + MinutesToTimeString(e.minutes) + "\n"
                    newString += "   Mitarbeiter: " + e.person + "\n";
                    newString += "   Datum: " + moment(e.date).format("DD.MM.Y") + "\n\n";
                }
            });
    
            newString += "\n"; // Leerzeile nach jeder Breadcrumb-Gruppe
        });
    
        // CSV in eine formatierbare Zeichenkette umwandeln
        let csvText = csv.join("\n");
        setCsvFormat(csvText);
    
        // Setze das Textformat
        setTextFormat(
            ExpenseBucket.sections[props.uuid].name +
            " (Gesamt: " + MinutesToTimeString(newMinuteSum) + ")\n\n" +
            newString
        );
    }, [ExpenseBucket]);

    const copyTableToClipboard = () => {
        const tempTextArea = document.createElement("textarea");
        tempTextArea.value = csvFormat;
        document.body.appendChild(tempTextArea);
    
        // Text im Textbereich auswählen und kopieren
        tempTextArea.select();
        const copySuccess = document.execCommand("copy");
    
        // Temporären Textbereich entfernen
        document.body.removeChild(tempTextArea);
    
        // Überprüfung, ob das Kopieren erfolgreich war
        if (copySuccess) {
            alert("Das Kopieren war erfolgreich!");
        } else {
            alert("Das Kopieren ist fehlgeschlagen. Bitte versuche es erneut.");
        }
    };

    const markAsAccounted = () => {
        axios({
            method: "post",
            auth: Auth.getAuthObject(),
            url: SyncConstants["syncBaseURL"] + "markAsAccounted",
            responseType: 'json',
            data: { entry_ids: ExpenseBucket.sections[props.uuid].expenses }
        }).then((response) => {
            props.forceTreeReload();
            ExpenseBucket.sections[props.uuid].expenses.forEach(entry => {
                dispatch(remove_entry(entry.id))
            });
            alert("Die Aufwände wurden serverseitig als 'Abgerechnet' markiert. Sie werden in Slotify aktualisiert, sobald die Maske aktualisiert wurde.");

        }).catch(response => { });
    }

    const createLocalInvoice = () => {
        if (!weclappDescription) {
            alert("Bitte geben Sie eine Rechnungsbeschreibung ein!");
            return;
        }

        // Sammle alle Entry IDs - unterscheide zwischen regulären Entries und External Costs
        const entryIds = [];
        const externalCostIds = [];
        
        ExpenseBucket.sections[props.uuid].expenses.forEach(e => {
            if (e.type === 'externalcost') {
                externalCostIds.push(e.id);
            } else {
                entryIds.push(e.id);
            }
        });

        // Erstelle lokale Rechnung
        axios({
            method: "post",
            url: SyncConstants["syncBaseURL"] + "invoice",
            auth: Auth.getAuthObject(),
            responseType: 'json',
            data: {
                entryIds: entryIds,
                externalCostIds: externalCostIds,
                description: weclappDescription
            }
        }).then((response) => {
            if (response.data.success) {
                const invoiceId = response.data.data.id;

                // Wenn direkter WeClapp-Export aktiviert ist
                if (directExportToWeClapp && weclappCustomerId) {
                    sendInvoiceToWeClapp(invoiceId);
                } else {
                    alert(`Rechnung #${invoiceId} wurde erfolgreich erstellt!`);
                    setShowModal(false);
                    // Entferne die Einträge aus dem Bucket, da sie jetzt einer Rechnung zugeordnet sind
                    ExpenseBucket.sections[props.uuid].expenses.forEach(entry => {
                        dispatch(remove_entry(entry.id))
                    });
                    props.forceTreeReload();
                }
            } else {
                // Zeige detaillierten Fehler vom Backend
                const errorMsg = response.data.error || "Unbekannter Fehler";
                alert("❌ Fehler beim Erstellen der Rechnung\n\n" + errorMsg);
            }
        }).catch(error => {
            console.error("Invoice Error:", error);
            
            // Prüfe ob es eine Antwort vom Server gibt
            if (error.response && error.response.data && error.response.data.error) {
                alert("❌ Fehler beim Erstellen der Rechnung\n\n" + error.response.data.error);
            } else {
                alert("❌ Fehler beim Erstellen der Rechnung\n\nNetzwerkfehler: " + error.message + "\n\nBitte überprüfen Sie Ihre Verbindung und versuchen Sie es erneut.");
            }
        });
    }

    const sendInvoiceToWeClapp = (invoiceId) => {
        if (!weclappCustomerId) {
            alert("Bitte WeClapp-Kunden-ID eingeben!");
            return;
        }

        axios({
            method: "post",
            url: SyncConstants["syncBaseURL"] + "invoice/send_to_weclapp",
            auth: Auth.getAuthObject(),
            data: {
                invoiceId: invoiceId,
                customerId: weclappCustomerId,
                articleId: weclappArticleId
            }
        }).then(response => {
            if (response.data.success) {
                alert(`Rechnung #${invoiceId} wurde erfolgreich erstellt und an WeClapp gesendet! WeClapp-ID: ${response.data.weclappData.id}`);
                setShowModal(false);
                // Entferne die Einträge aus dem Bucket
                ExpenseBucket.sections[props.uuid].expenses.forEach(entry => {
                    dispatch(remove_entry(entry.id))
                });
                props.forceTreeReload();
            } else {
                const errorMsg = response.data.error || "Unbekannter Fehler";
                alert("⚠️ Rechnung wurde erstellt, aber konnte nicht an WeClapp gesendet werden\n\n" + errorMsg + "\n\nDie Rechnung wurde lokal gespeichert und kann später manuell an WeClapp gesendet werden.");
                setShowModal(false);
            }
        }).catch(error => {
            console.error("WeClapp Error:", error);
            
            // Prüfe ob es eine Antwort vom Server gibt
            if (error.response && error.response.data && error.response.data.error) {
                alert("⚠️ Rechnung wurde erstellt, aber konnte nicht an WeClapp gesendet werden\n\n" + error.response.data.error + "\n\nDie Rechnung wurde lokal gespeichert und kann später manuell an WeClapp gesendet werden.");
            } else {
                alert("⚠️ Rechnung wurde erstellt, aber konnte nicht an WeClapp gesendet werden\n\nNetzwerkfehler: " + error.message + "\n\nDie Rechnung wurde lokal gespeichert und kann später manuell an WeClapp gesendet werden.");
            }
            setShowModal(false);
        });
    }

    const handleCustomerSelect = (customer) => {
        setWeclappCustomerId(customer.id);
        setWeclappCustomerName(customer.company || customer.name || "");
        setShowCustomerSelector(false);
    };

    return <>
        <WeClappCustomerSelector
            isOpen={showCustomerSelector}
            onClose={() => setShowCustomerSelector(false)}
            onSelect={handleCustomerSelect}
            title="WeClapp-Kunden für Rechnung auswählen"
        />

        <li className={stylelist(
            "list-group-item",
            "bg-light"
        )}>

            {showModal && ReactDOM.createPortal(
                <div
                    className="overlay d-flex justify-content-center align-items-center"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 9999,
                        backgroundColor: 'rgba(0,0,0,0.5)'
                    }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setShowModal(false);
                        }
                    }}
                >
                    <div className="modal d-block">
                        <div className="modal-dialog" style={{ minWidth: "1024px" }}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        <i className="bi bi-receipt me-2"></i>
                                        {ExpenseBucket.sections[props.uuid].name} abrechnen
                                    </h5>
                                    <button type="button" className="btn-close" onClick={() => { setShowModal(false) }} aria-label="Close"></button>
                                </div>
                                <div className="modal-body">
                                    {/* Exportmethode wählen */}
                                    <div className="mb-4">
                                        <label className="form-label fw-bold">Export-Methode wählen</label>
                                        <div className="btn-group w-100" role="group">
                                            <button
                                                type="button"
                                                className={`btn ${currentModalTab === "invoice" ? "btn-primary" : "btn-outline-primary"}`}
                                                onClick={() => setCurrentModalTab("invoice")}
                                            >
                                                <i className="bi bi-receipt-cutoff me-2"></i>
                                                Rechnung erstellen
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn ${currentModalTab === "text" ? "btn-primary" : "btn-outline-primary"}`}
                                                onClick={() => setCurrentModalTab("text")}
                                            >
                                                <i className="bi bi-file-text me-2"></i>
                                                Formatierter Text
                                            </button>
                                            <button
                                                type="button"
                                                className={`btn ${currentModalTab === "csv" ? "btn-primary" : "btn-outline-primary"}`}
                                                onClick={() => setCurrentModalTab("csv")}
                                            >
                                                <i className="bi bi-file-earmark-spreadsheet me-2"></i>
                                                Excel
                                            </button>
                                        </div>
                                    </div>

                                    {currentModalTab == "text" &&
                                        <>
                                            <textarea className="form-control" value={textFormat} style={{ minWidth: "924px", minHeight: "500px" }} />
                                        </>
                                    }

                                    {currentModalTab == "csv" &&
                                        <>
                                            <p>Die gewählten Buchungen werden in die Zwischenablage kopiert und können dann in ein Excel eingefügt werden.</p>
                                            <div className="btn btn-primary" onClick={() => {copyTableToClipboard()}}>Kopieren</div>
                                        </>
                                    }


                                    {currentModalTab == "invoice" &&
                                        <>
                                            <div className="mb-3">
                                                <label className="form-label fw-semibold">
                                                    <i className="bi bi-file-text me-1"></i>
                                                    Rechnungsbeschreibung
                                                </label>
                                                <textarea
                                                    className="form-control"
                                                    value={weclappDescription}
                                                    onChange={(e) => setWeclappDescription(e.target.value)}
                                                    placeholder="Beschreibung für die Rechnung eingeben..."
                                                    rows="3"
                                                />
                                            </div>

                                            {/* WeClapp Export Checkbox und Kunden-ID */}
                                            <div className="card border-primary mb-3">
                                                <div className="card-header bg-light">
                                                    <div className="form-check form-switch">
                                                        <input
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            id="directWeClappExport"
                                                            checked={directExportToWeClapp}
                                                            onChange={(e) => setDirectExportToWeClapp(e.target.checked)}
                                                        />
                                                        <label className="form-check-label fw-semibold" htmlFor="directWeClappExport">
                                                            <i className="bi bi-cloud-upload me-1"></i>
                                                            Direkt nach WeClapp exportieren
                                                        </label>
                                                    </div>
                                                </div>
                                                {directExportToWeClapp && (
                                                    <div className="card-body">
                                                        <label className="form-label">
                                                            <i className="bi bi-building me-1"></i>
                                                            WeClapp Kunden-ID
                                                        </label>
                                                        <div className="input-group">
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={weclappCustomerId}
                                                                onChange={(e) => setWeclappCustomerId(e.target.value)}
                                                                placeholder="z.B. 5663644"
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-primary"
                                                                onClick={() => setShowCustomerSelector(true)}
                                                                title="WeClapp-Kunden auswählen"
                                                            >
                                                                <i className="bi bi-search"></i> Suchen
                                                            </button>
                                                        </div>
                                                        {weclappCustomerName && (
                                                            <small className="form-text text-success">
                                                                <i className="bi bi-check-circle me-1"></i>
                                                                Kunden-ID von Kunde "{weclappCustomerName}" übernommen
                                                            </small>
                                                        )}
                                                        <small className="form-text text-muted d-block mt-1">
                                                            {weclappCustomerId
                                                                ? "Die Rechnung wird nach Erstellung automatisch an WeClapp gesendet."
                                                                : "Bitte wählen Sie einen Kunden aus, um die Rechnung direkt an WeClapp zu senden."}
                                                        </small>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="mb-3">
                                                <h6 className="mb-3">
                                                    <i className="bi bi-list-check me-2"></i>
                                                    Buchungssätze (gruppiert nach Abschnitten)
                                                </h6>
                                                {(() => {
                                                    // Gruppiere die Einträge nach Breadcrumb
                                                    const groupedByBreadcrumb = {};
                                                    ExpenseBucket.sections[props.uuid].expenses.forEach(e => {
                                                        const breadcrumbKey = e.breadcrumb || 'Externe Kosten';
                                                        if (!groupedByBreadcrumb[breadcrumbKey]) {
                                                            groupedByBreadcrumb[breadcrumbKey] = {
                                                                totalMinutes: 0,
                                                                totalAmount: 0,
                                                                entries: []
                                                            };
                                                        }
                                                        if (e.type === 'externalcost') {
                                                            groupedByBreadcrumb[breadcrumbKey].totalAmount += parseFloat(e.amount || 0);
                                                        } else {
                                                            groupedByBreadcrumb[breadcrumbKey].totalMinutes += parseInt(e.minutes);
                                                        }
                                                        groupedByBreadcrumb[breadcrumbKey].entries.push(e);
                                                    });

                                                    return (
                                                        <div className="border rounded p-3 bg-light" style={{ maxHeight: "400px", overflowY: "auto" }}>
                                                            {Object.keys(groupedByBreadcrumb).map((breadcrumb, index) => {
                                                                const group = groupedByBreadcrumb[breadcrumb];
                                                                
                                                                // Build summary string
                                                                let summaryParts = [];
                                                                if (group.totalMinutes > 0) {
                                                                    summaryParts.push(MinutesToTimeString(group.totalMinutes));
                                                                }
                                                                if (group.totalAmount > 0) {
                                                                    summaryParts.push(group.totalAmount.toFixed(2) + ' €');
                                                                }
                                                                const summary = summaryParts.length > 0 ? summaryParts.join(' + ') : '0:00';
                                                                
                                                                return (
                                                                    <div key={index} className="mb-4">
                                                                        <div className="fw-bold text-primary mb-2">
                                                                            {breadcrumb}
                                                                            <span className="text-muted ms-2">
                                                                                (Gesamt: {summary})
                                                                            </span>
                                                                        </div>
                                                                        <ul className="list-unstyled ps-3">
                                                                            {group.entries.map((entry, entryIndex) => (
                                                                                <li key={entryIndex} className="mb-1 text-muted small">
                                                                                    • {entry.type === 'externalcost' ? entry.description : entry.comment} - {
                                                                                        entry.type === 'externalcost' 
                                                                                            ? parseFloat(entry.amount).toFixed(2) + ' €'
                                                                                            : MinutesToTimeString(entry.minutes)
                                                                                    }
                                                                                    <span className="ms-2">({entry.person}, {moment(entry.date).format("DD.MM.YYYY")})</span>
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    );
                                                })()}
                                            </div>

                                            <div className="mt-3">
                                                <button
                                                    className="btn btn-primary btn-lg w-100"
                                                    onClick={createLocalInvoice}
                                                    disabled={!weclappDescription || (directExportToWeClapp && !weclappCustomerId)}
                                                >
                                                    <i className="bi bi-receipt-cutoff me-2"></i>
                                                    {directExportToWeClapp
                                                        ? "Rechnung erstellen & an WeClapp senden"
                                                        : "Rechnung erstellen"}
                                                </button>
                                                {!weclappDescription && (
                                                    <small className="text-danger d-block mt-2">
                                                        <i className="bi bi-exclamation-triangle me-1"></i>
                                                        Bitte Rechnungsbeschreibung eingeben
                                                    </small>
                                                )}
                                                {directExportToWeClapp && !weclappCustomerId && (
                                                    <small className="text-danger d-block mt-2">
                                                        <i className="bi bi-exclamation-triangle me-1"></i>
                                                        Bitte WeClapp-Kunden auswählen
                                                    </small>
                                                )}
                                            </div>
                                        </>
                                    }
                                </div>
                                <div className="modal-footer border-0">
                                    {(currentModalTab === "text" || currentModalTab === "csv") && (
                                        <ConfirmButton className="btn btn-primary cursor-pointer" labelConfirm="Zum Server senden" label="Als 'abgerechnet' markieren" onConfirm={e => { markAsAccounted() }} />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <div className="d-flex justify-content-between align-items-center p-2">
                <div className="flex-grow-1">
                    {!editName &&
                        <div className="d-flex align-items-center">
                            <input
                                checked={ExpenseBucket.currentSection == props.uuid ? true : false}
                                onClick={() => { dispatch(set_current_section(props.uuid)) }}
                                className="form-check-input me-2"
                                type="radio"
                                name="flexRadioDefault"
                                id={`section-${props.uuid}`}
                            />
                            <label
                                htmlFor={`section-${props.uuid}`}
                                className="cursor-pointer mb-0 fw-semibold"
                            >
                                {ExpenseBucket.sections[props.uuid].name}
                                {Boolean(ExpenseBucket.sections[props.uuid].expenses.length) && (
                                    <span className="badge bg-primary ms-2">
                                        {ExpenseBucket.sections[props.uuid].expenses.length}
                                    </span>
                                )}
                            </label>
                        </div>
                    }

                    {editName &&
                        <input
                            className="form-control form-control-sm"
                            type="text"
                            value={ExpenseBucket.sections[props.uuid].name}
                            onChange={(e) => { dispatch(rename_section(props.uuid, e.target.value)) }}
                            autoFocus
                        />
                    }
                </div>

                <div className="ms-2 d-flex gap-2">
                    {!editName &&
                        <>
                            <button
                                className="btn btn-sm btn-link text-decoration-none p-0"
                                onClick={() => { setEditName(true) }}
                                title="Umbenennen"
                            >
                                <i className="bi bi-pencil"></i>
                            </button>
                            <button
                                className="btn btn-sm btn-link text-danger text-decoration-none p-0"
                                onClick={() => {
                                    if (confirm(`Bucket "${ExpenseBucket.sections[props.uuid].name}" wirklich löschen?`)) {
                                        // Lösche alle Slots der Einträge in diesem Bucket
                                        ExpenseBucket.sections[props.uuid].expenses.forEach(entry => {
                                            if (entry.slotId) {
                                                dispatch(delete_slot(entry.slotId));
                                            }
                                        });
                                        
                                        // Lösche den Bucket (entfernt auch IDs aus addedIds via Reducer)
                                        dispatch(remove_section(props.uuid));
                                    }
                                }}
                                title="Bucket löschen"
                            >
                                <i className="bi bi-trash"></i>
                            </button>
                        </>
                    }

                    {Boolean(editName) &&
                        <button
                            className="btn btn-sm btn-success"
                            onClick={() => { setEditName(false) }}
                            title="Speichern"
                        >
                            <i className="bi bi-check-lg"></i>
                        </button>
                    }
                </div>
            </div>

            {!editName &&
                <>
                    {Boolean(ExpenseBucket.sections[props.uuid].expenses.length) ? (
                        <>
                            <div className="px-2">
                                <div className="list-group list-group-flush">
                                    {ExpenseBucket.sections[props.uuid].expenses.map(e => {
                                        return (
                                            <div key={e.id} className="list-group-item px-2 py-3 d-flex justify-content-between align-items-start">
                                                <div className="flex-grow-1">
                                                    <div className="fw-semibold small">
                                                        {e.type === 'externalcost' ? e.description : e.comment}
                                                    </div>
                                                    <div className="text-muted small mt-1">
                                                        {e.type === 'externalcost' ? (
                                                            <>
                                                                <i className="bi bi-currency-euro me-1"></i>
                                                                {parseFloat(e.amount).toFixed(2)} €
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="bi bi-clock me-1"></i>
                                                                <MinutesToTime minutes={e.minutes} />
                                                            </>
                                                        )}
                                                        <span className="mx-2">•</span>
                                                        <i className="bi bi-calendar3 me-1"></i>
                                                        {moment(e.date).format("DD.MM.YY")}
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => { dispatch(remove_entry(e.id)) }}
                                                    className="btn btn-sm btn-link text-danger p-0 ms-2"
                                                    title="Entfernen"
                                                >
                                                    <i className="bi bi-x-lg"></i>
                                                </button>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>

                            <div className="p-2 d-grid">
                                <button
                                    className="btn btn-primary"
                                    onClick={() => { setShowModal(true) }}
                                >
                                    <i className="bi bi-box-arrow-up-right me-2"></i>
                                    Exportieren
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="text-muted small text-center py-3 px-2">
                            <i className="bi bi-inbox"></i>
                            <div>Keine Einträge</div>
                        </div>
                    )}
                </>
            }

        </li>
    </>
}

export default ExpenseBucketSection;